import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Search } from "lucide-react";

import { getArchivedInvoices, unArchiveInvoice } from "src/api/invoice";
import useDebounce from "src/hooks/useDebounce";
import CustomModal from "src/components/CustomModal";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import { DataTable, StatusBadge, type Column, type RowsPerPageOption } from "src/components/common";
import { renderCurrency, showSuccessToast } from "src/lib/utils";
import { invoiceStatusBadge, type InvoiceRow } from "src/types/invoice";

interface ArchivedInvoicesModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	invoiceLabel: string;
}

const formatDate = (value?: string) => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "MMM dd, yyyy");
};

/** Archived invoices as a modal (decision D2). `/invoices/archived` redirects here via `?archived=1`. */
const ArchivedInvoicesModal = ({ open, onOpenChange, invoiceLabel }: ArchivedInvoicesModalProps) => {
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState<RowsPerPageOption>(10);

	const handleSearch = useDebounce((term: string) => {
		setSearch(term);
		setPage(1);
	}, 500);

	const { data, isLoading } = useQuery({
		queryKey: ["invoices", "archived", page, limit, search],
		queryFn: () => getArchivedInvoices({ page, limit: limit === "All" ? 9999 : limit, search }),
		enabled: open,
	});

	const { mutate: restoreInvoice } = useMutation(unArchiveInvoice, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || `${invoiceLabel} restored`);
			queryClient.invalidateQueries(["invoices"]);
		},
	});

	const list = data?.data;
	const invoices = list?.data ?? [];

	const columns: Column<InvoiceRow>[] = [
		{ key: "no", header: `${invoiceLabel} No`, primary: true, cell: (row) => row.invoice_id ?? "—" },
		{ key: "customer", header: "Customer", cell: (row) => [row.first_name, row.last_name].filter(Boolean).join(" ") || "—" },
		{ key: "total", header: "Total", cell: (row) => renderCurrency(row.total_amount ?? 0) },
		{ key: "due", header: "Due Date", cell: (row) => formatDate(row.due_date) },
		{ key: "status", header: "Status", cell: (row) => <StatusBadge status={invoiceStatusBadge(row.status)} /> },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[110px] tw-text-right",
			cell: (row) => (
				<CustomConfirmDialog
					trigger={
						<button
							type="button"
							className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
						>
							Restore
						</button>
					}
					mutateFn={() => restoreInvoice({ uuid: row.uuid })}
				/>
			),
		},
	];

	return (
		<CustomModal open={open} onOpenChange={onOpenChange} size="lg" title={`Archived ${invoiceLabel}`}>
			<div className="tw-space-y-4">
				<div className="tw-relative tw-w-full sm:tw-w-[280px]">
					<Search className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-h-4 tw-w-4 tw-text-text-soft" />
					<input
						type="search"
						placeholder="Search archived invoices..."
						onChange={(event) => handleSearch(event.target.value.trim())}
						className="tw-h-10 tw-w-full tw-rounded-[10px] tw-border tw-border-border tw-bg-white tw-pl-9 tw-pr-3 tw-text-[13.5px] tw-text-text placeholder:tw-text-text-soft focus:tw-border-teal focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-teal/20"
					/>
				</div>

				<DataTable
					data={invoices}
					columns={columns}
					rowKey={(row) => row.uuid}
					loading={isLoading}
					totalRows={list?.total ?? invoices.length}
					page={page}
					onPageChange={setPage}
					rowsPerPage={limit}
					onRowsPerPageChange={setLimit}
					emptyTitle="No archived invoices"
					emptyDescription="Invoices you archive will be listed here and can be restored."
				/>
			</div>
		</CustomModal>
	);
};

export default ArchivedInvoicesModal;
